import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT, DURATION_SLOW } from '@/lib/motion';
import { toast } from '@/components/ui/sonner';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { calculateHalfDayRate } from '@/utils/calculateHalfDayRate';
import { findRoleIndex } from '@/utils/findRoleIndex';
import { useIsDesktop } from '@/hooks/use-mobile';
import type { Role } from '@/types/Role';
import type { RoleListProps, RoleLiveValues } from './types';
import { RoleListItem } from './RoleListItem';
import { RoleInlineForm } from './RoleInlineForm';
import { RoleDetailSheet } from './RoleDetailSheet';

export function RoleList({
  roles,
  onRolesChange,
  onRemove,
  onHasDayRateChange,
  onHasHalfDayChange,
  onEditingChange,
}: RoleListProps) {
  // ── State ──────────────────────────────────────────────
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [liveEditValues, setLiveEditValues] = useState<RoleLiveValues | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('edit');
  const [sheetRole, setSheetRole] = useState<Role | null>(null);

  // Track which card is mid-collapse so it keeps expanded styling during animation.
  const [collapsingIndex, setCollapsingIndex] = useState<number | null>(null);
  const prevExpandedRef = useRef(expandedIndex);
  const collapsingTimerRef = useRef<ReturnType<typeof setTimeout>>();
  if (prevExpandedRef.current !== expandedIndex) {
    const prev = prevExpandedRef.current;
    prevExpandedRef.current = expandedIndex;
    // Clear stale live values immediately when switching expanded rows
    if (expandedIndex !== null && prev !== null && prev >= 0) {
      setLiveEditValues(null);
    }
    if (prev !== null && prev !== expandedIndex && prev >= 0) {
      setCollapsingIndex(prev);
      clearTimeout(collapsingTimerRef.current);
      collapsingTimerRef.current = setTimeout(() => {
        setCollapsingIndex(null);
        setLiveEditValues(null);
      }, 300);
    }
  }

  const expandedCardRef = useRef<HTMLDivElement>(null);
  const addFormRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsDesktop();

  // ── Derived flags ──────────────────────────────────────
  const isAddMode = expandedIndex === -1;

  const hasDayRate = useMemo(
    () => roles.some((r) => r.billingStructure === 'day_rate'),
    [roles],
  );
  const hasHalfDay = useMemo(
    () =>
      roles.some(
        (r) => r.billingStructure === 'day_rate' && r.allowHalfDay && calculateHalfDayRate(r) !== null,
      ),
    [roles],
  );

  // ── Callbacks: editing state ───────────────────────────
  useEffect(() => {
    onEditingChange?.(expandedIndex !== null);
  }, [expandedIndex, onEditingChange]);

  // ── Callbacks: day-rate / half-day flags (Fix 4) ──────
  useEffect(() => {
    onHasDayRateChange?.(hasDayRate);
  }, [hasDayRate, onHasDayRateChange]);

  useEffect(() => {
    onHasHalfDayChange?.(hasHalfDay);
  }, [hasHalfDay, onHasHalfDayChange]);

  // ── Escape key ─────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && expandedIndex !== null) {
        setExpandedIndex(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [expandedIndex]);

  // Track the last added/modified role for pre-filling new role forms
  const lastSavedRoleRef = useRef<Role | null>(null);

  // ── Shared save/delete helpers (Fix 3) ────────────────
  const applyRoleSave = useCallback(
    (newRole: Role, originalRole?: Role): void => {
      lastSavedRoleRef.current = newRole;
      if (originalRole) {
        const idx = findRoleIndex(roles, originalRole);
        if (idx >= 0) {
          const updated = [...roles];
          updated[idx] = newRole;
          onRolesChange(updated);
          toast.success(`Updated "${newRole.name}"`, { id: 'role-change' });
        }
      } else {
        onRolesChange([...roles, newRole]);
        toast.success(`Added "${newRole.name}"`, { id: 'role-change' });
      }
    },
    [roles, onRolesChange],
  );

  const applyRoleDelete = useCallback(
    (role: Role): void => {
      const idx = findRoleIndex(roles, role);
      if (idx !== -1) {
        onRemove(idx);
      }
    },
    [roles, onRemove],
  );

  // ── Click handler: mobile sheet vs desktop expand (Fix 5) ──
  const handleCardClick = useCallback(
    (index: number) => {
      // Blur before toggling — prevents aria-hidden focus warning when
      // AnimatedCollapse hides the element that currently holds focus
      (document.activeElement as HTMLElement)?.blur?.();
      if (!isDesktop) {
        setSheetRole(roles[index]);
        setSheetMode('edit');
        setSheetOpen(true);
      } else {
        // Desktop — toggle expand
        setExpandedIndex((prev) => (prev === index ? null : index));
      }
    },
    [roles, isDesktop],
  );

  const handleAddClick = useCallback(() => {
    (document.activeElement as HTMLElement)?.blur?.();
    if (!isDesktop) {
      setSheetRole(null);
      setSheetMode('create');
      setSheetOpen(true);
    } else {
      setExpandedIndex(-1);
    }
  }, [isDesktop]);

  // ── Save handler (inline form) ─────────────────────────
  const handleInlineSave = useCallback(
    (newRole: Role, originalRole?: Role) => {
      applyRoleSave(newRole, originalRole);
      setExpandedIndex(null);
    },
    [applyRoleSave],
  );

  // ── Delete handler (inline form) ──────────────────────
  const handleInlineDelete = useCallback(
    (role: Role) => {
      applyRoleDelete(role);
      setExpandedIndex(null);
    },
    [applyRoleDelete],
  );

  // ── Save handler (sheet) ───────────────────────────────
  const handleSheetSave = useCallback(
    (newRole: Role, originalRole?: Role) => {
      applyRoleSave(newRole, originalRole);
      setSheetOpen(false);
    },
    [applyRoleSave],
  );

  // ── Delete handler (sheet) ────────────────────────────
  const handleSheetDelete = useCallback(
    (role: Role) => {
      applyRoleDelete(role);
      setSheetOpen(false);
    },
    [applyRoleDelete],
  );

  // ── Render ─────────────────────────────────────────────
  return (
    <div className="flex flex-col" data-role-list>
      {/* Card list */}
      <div className="flex flex-col gap-2">
        <AnimatePresence initial={false}>
          {roles.map((role, index) => {
            const isExpanded = expandedIndex === index;
            const isCollapsing = collapsingIndex === index;
            const showExpanded = isExpanded || isCollapsing;

            return (
              <motion.div
                key={role.id ?? `role-${index}`}
                initial={{ height: 0, opacity: 0, overflow: 'hidden' }}
                animate={{ height: 'auto', opacity: 1, overflow: 'visible' }}
                transition={{ duration: DURATION_SLOW, ease: EASE_EXPO_OUT }}
              >
                <div
                  ref={isExpanded ? expandedCardRef : undefined}
                  className={cn(
                    'rounded',
                    showExpanded
                      ? 'overflow-hidden border border-primary/60 border-l-2 border-l-primary bg-surface-1 dark:bg-transparent'
                      : 'overflow-visible',
                  )}
                >
                  <RoleListItem
                    role={role}
                    index={index}
                    isExpanded={showExpanded}
                    onCardClick={handleCardClick}
                    liveOverrides={showExpanded && liveEditValues ? liveEditValues : undefined}
                  />
                  <AnimatedCollapse open={isExpanded}>
                    <RoleInlineForm
                      key={`edit-${index}`}
                      role={role}
                      roles={roles}
                      onSave={handleInlineSave}
                      onCancel={() => setExpandedIndex(null)}
                      onDelete={handleInlineDelete}
                      onLiveValuesChange={setLiveEditValues}
                    />
                  </AnimatedCollapse>
                </div>
              </motion.div>
            );
          })}

          {/* Add mode form (desktop) */}
          {isAddMode && (
            <motion.div
              key="add-form"
              ref={addFormRef}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: DURATION_SLOW, ease: EASE_EXPO_OUT }}
              onAnimationComplete={(definition) => {
                if (definition === 'animate') {
                  addFormRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                  });
                }
              }}
              className="overflow-hidden"
            >
              <div className="border border-primary/60 border-l-2 border-l-primary rounded bg-surface-1 dark:bg-transparent">
                <RoleInlineForm
                  role={null}
                  roles={roles}
                  lastSavedRole={lastSavedRoleRef.current}
                  onSave={handleInlineSave}
                  onCancel={() => setExpandedIndex(null)}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Add button / empty state */}
      <AnimatedCollapse open={!isAddMode && expandedIndex === null}>
        {roles.length === 0 ? (
          /* Empty state CTA */
          <div className="flex flex-col items-center pt-2 gap-2 sm:pt-4 sm:gap-3">
            <button
              type="button"
              onClick={handleAddClick}
              className="animate-pulse-amber inline-flex items-center gap-1 text-xs font-mono text-primary-text border border-primary/40 hover:border-primary/60 rounded px-4 py-2 cursor-pointer"
            >
              <Plus aria-hidden="true" className="h-3 w-3" /> Add Role
            </button>
            <p className="text-xs font-mono text-muted-foreground italic">
              <span className="text-primary-text">tip:</span> Add your primary
              roles — e.g., A1, Stage Manager, Patch Tech
            </p>
          </div>
        ) : (
          /* Add buttons when roles exist */
          <div className="flex flex-col items-center pt-2 gap-2 sm:pt-4 sm:gap-3">
            {/* Mobile add button */}
            <button
              type="button"
              onClick={handleAddClick}
              className="flex sm:hidden items-center justify-center gap-2 w-full mt-2 py-3 rounded border border-dashed border-primary/30 text-primary-text text-sm font-medium cursor-pointer hover:border-primary/50 transition-colors"
            >
              + Add Role
            </button>
            {/* Desktop add button */}
            <button
              type="button"
              onClick={handleAddClick}
              className="hidden sm:inline-flex items-center gap-1 text-[10px] sm:text-[12px] font-mono text-primary-text hover:text-primary-text transition-colors cursor-pointer border border-border/50 hover:border-primary/40 rounded px-3 py-2"
            >
              <Plus aria-hidden="true" className="h-3 w-3" /> Add Role
            </button>
          </div>
        )}
      </AnimatedCollapse>

      {/* Mobile sheet */}
      <RoleDetailSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        mode={sheetMode}
        role={sheetRole}
        roles={roles}
        lastSavedRole={lastSavedRoleRef.current}
        onSave={handleSheetSave}
        onDelete={handleSheetDelete}
      />
    </div>
  );
}
